import { supabase } from './supabase';

export interface CustomerVehicle {
  id: string;
  customer_id: string;
  make: string;
  model: string;
  year: number;
  vin: string | null;
  color: string | null;
  license_plate: string | null;
  mileage: number | null;
  notes: string | null;
  is_primary: boolean;
  last_service: string | null;
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: string;
  business_id: string;
  type: 'person' | 'business';
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  source: string | null;
  tags: string[];
  last_interaction: string | null;
  lifetime_value: number;
  next_service_date: string | null;
  referral_source: string | null;
  preferences: Record<string, any>;
  status: string;
  amount_spent: number;
  credit_balance: number;
  notes: string | null;
  tax_exempt: boolean;
  workflow_exempt: boolean;
  carfax_exempt: boolean;
  receive_reminders: boolean;
  receive_offers: boolean;
  is_supplier: boolean;
  repeat: boolean;
  created_at: string;
  updated_at: string;
  vehicles?: CustomerVehicle[];
  total_bookings?: number;
  total_vehicles?: number;
  total_spent?: number;
  last_booking_date?: string;
  recent_bookings?: {
    id: string;
    booking_number: number;
    title: string;
    status: string;
    total_amount: number;
    created_at: string;
  }[];
}

export async function getCustomers(businessId: string) {
  const { data, error } = await supabase
    .from('customers')
    .select(`
      *,
      vehicles:customer_vehicles(*)
    `)
    .eq('business_id', businessId)
    .eq('status', 'active')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Customer[];
}

export async function getCustomer(id: string) {
  if (!id || typeof id !== 'string') {
    throw new Error('Customer ID is required');
  }

  const { data, error } = await supabase
    .from('customer_summary')
    .select(`
      *,
      vehicles:customer_vehicles(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching customer:', error);
    throw new Error('Unable to load customer details');
  }

  if (!data) {
    throw new Error('Customer not found');
  }

  return data as Customer;
}

/**
 * Checks if a customer with the given email or phone already exists (active customers only)
 * @returns Object with isDuplicate flag and matchingCustomer data if found
 */
export async function checkDuplicateCustomer(
  businessId: string,
  email?: string | null,
  phone?: string | null
) {
  // Clean and validate inputs - ignore empty strings
  const cleanEmail = email?.trim() || null;
  const cleanPhone = phone?.trim() || null;
  
  if (!cleanEmail && !cleanPhone) {
    console.log('🚫 [DUPLICATE CHECK] No valid email or phone provided - skipping check');
    return { isDuplicate: false, matchingCustomer: null };
  }

  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  console.log('🔍 [DUPLICATE CHECK] Input params (cleaned):', { businessId, email: cleanEmail, phone: cleanPhone });
  console.log(`🏢 [DUPLICATE CHECK] Checking ONLY business: ${businessId}`);

  // Handle single field queries
  if (cleanEmail && !cleanPhone) {
    console.log('📧 [DUPLICATE CHECK] Checking email only:', cleanEmail);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('status', 'active')
      .eq('email', cleanEmail);

    if (error) {
      console.error('❌ [DUPLICATE CHECK] Email query error:', error);
      throw new Error('Unable to check for duplicate customers (email)');
    }

    const isDuplicate = data && data.length > 0;
    const matchingCustomer = isDuplicate ? data[0] : null;
    
    console.log(`📊 [DUPLICATE CHECK] Email result: ${isDuplicate ? `FOUND "${matchingCustomer?.first_name} ${matchingCustomer?.last_name}" (${matchingCustomer?.email})` : 'NOT FOUND'}`);

    return {
      isDuplicate,
      matchingCustomer
    };
  }

  if (cleanPhone && !cleanEmail) {
    console.log('📱 [DUPLICATE CHECK] Checking phone only:', cleanPhone);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('status', 'active')
      .eq('phone', cleanPhone);

    if (error) {
      console.error('❌ [DUPLICATE CHECK] Phone query error:', error);
      throw new Error('Unable to check for duplicate customers (phone)');
    }

    const isDuplicate = data && data.length > 0;
    const matchingCustomer = isDuplicate ? data[0] : null;
    
    console.log(`📊 [DUPLICATE CHECK] Phone result: ${isDuplicate ? `FOUND "${matchingCustomer?.first_name} ${matchingCustomer?.last_name}" (${matchingCustomer?.phone})` : 'NOT FOUND'}`);

    return {
      isDuplicate,
      matchingCustomer
    };
  }

  // Handle both email and phone (original OR logic)
  if (cleanEmail && cleanPhone) {
    console.log('📧📱 [DUPLICATE CHECK] Checking both email and phone');
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('status', 'active')
      .or(`email.eq.${cleanEmail},phone.eq.${cleanPhone}`);

    if (error) {
      console.error('❌ [DUPLICATE CHECK] Combined query error:', error);
      throw new Error('Unable to check for duplicate customers (both)');
    }

    return {
      isDuplicate: data && data.length > 0,
      matchingCustomer: data && data.length > 0 ? data[0] : null
    };
  }

  return { isDuplicate: false, matchingCustomer: null };
}

/**
 * Checks if a customer with the given email or phone exists in deleted status
 * @returns Object with isArchived flag and archivedCustomer data if found
 */
export async function checkArchivedCustomer(
  businessId: string,
  email?: string | null,
  phone?: string | null
) {
  if (!email && !phone) {
    return { isArchived: false, archivedCustomer: null };
  }

  const query = supabase!
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'deleted');

  if (email && phone) {
    query.or(`email.eq.${email},phone.eq.${phone}`);
  } else if (email) {
    query.eq('email', email);
  } else if (phone) {
    query.eq('phone', phone);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error checking for deleted customer:', error);
    throw new Error('Unable to check for deleted customers');
  }

  return {
    isArchived: data && data.length > 0,
    archivedCustomer: data && data.length > 0 ? data[0] : null
  };
}

export async function createCustomer(
  customer: Omit<Customer, 'id' | 'created_at' | 'updated_at' | 'vehicles'>,
  vehicles?: Omit<CustomerVehicle, 'id' | 'customer_id' | 'created_at' | 'updated_at'>[]
) {

  // Check for active customer duplicates first
  const { isDuplicate, matchingCustomer } = await checkDuplicateCustomer(
    customer.business_id,
    customer.email,
    customer.phone
  );

  if (isDuplicate) {
    let duplicateFields = [];
    if (matchingCustomer?.email === customer.email && customer.email) {
      duplicateFields.push('email');
    }
    if (matchingCustomer?.phone === customer.phone && customer.phone) {
      duplicateFields.push('phone number');
    }
    
    throw new Error(
      `A customer with this ${duplicateFields.join(' and ')} already exists. `+
      `Customer: ${matchingCustomer?.first_name} ${matchingCustomer?.last_name}`
    );
  }

  // Check for archived customer conflicts
  const { isArchived, archivedCustomer } = await checkArchivedCustomer(
    customer.business_id,
    customer.email,
    customer.phone
  );

  if (isArchived) {
    let archivedFields = [];
    if (archivedCustomer?.email === customer.email && customer.email) {
      archivedFields.push('email');
    }
    if (archivedCustomer?.phone === customer.phone && customer.phone) {
      archivedFields.push('phone number');
    }
    
    throw new Error(
      `This ${archivedFields.join(' and ')} belongs to an archived customer: ${archivedCustomer?.first_name} ${archivedCustomer?.last_name}. Please contact support to reactivate the customer or use different contact information.`
    );
  }

  // Create customer
  const { data: customerData, error: customerError } = await supabase
    .from('customers')
    .insert([{
      ...customer,
      is_supplier: customer.is_supplier ?? false // Default to false if not specified
    }])
    .select()
    .single();

  if (customerError) throw customerError;

  // Create vehicles if provided
  if (vehicles && vehicles.length > 0) {
    const { error: vehiclesError } = await supabase
      .from('customer_vehicles')
      .insert(
        vehicles.map(vehicle => ({
          ...vehicle,
          customer_id: customerData.id
        }))
      );

    if (vehiclesError) throw vehiclesError;
  }

  // Auto-create map pin if customer has coordinates
  if (customerData.lat && customerData.lng && customerData.address) {
    try {
      console.log('🎯 Auto-creating map pin for new customer:', {
        name: `${customerData.first_name} ${customerData.last_name}`,
        address: customerData.address,
        coordinates: { lat: customerData.lat, lng: customerData.lng }
      });

      const { error: markerError } = await supabase
        .from('address_markers')
        .insert({
          business_id: customerData.business_id,
          customer_id: customerData.id,
          latitude: customerData.lat,
          longitude: customerData.lng,
          marker_type: 'customer',
          address: customerData.address,
          notes: `Customer: ${customerData.type === 'business' ? customerData.first_name : `${customerData.first_name} ${customerData.last_name}`.trim()}`,
          created_by: null
        });

      if (markerError) {
        console.error('❌ Failed to auto-create map pin:', markerError);
        // Don't throw error - customer creation should succeed even if pin creation fails
      } else {
        console.log('✅ Auto-created map pin for new customer');
      }
    } catch (error) {
      console.error('❌ Exception during auto map pin creation:', error);
      // Don't throw error - customer creation should succeed even if pin creation fails
    }
  }

  return getCustomer(customerData.id);
}

export async function updateCustomer(
  id: string, 
  updates: Partial<Customer> & { 
    vehicles?: (Omit<CustomerVehicle, 'id' | 'customer_id' | 'created_at' | 'updated_at'> & { id?: string })[]
  }
) {
  if (!id || typeof id !== 'string') {
    throw new Error('Invalid customer ID provided');
  }

  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Database connection is not available');
  }

  const { vehicles, ...customerUpdates } = updates;

  // Validate required fields
  if (!customerUpdates.first_name?.trim()) {
    throw new Error('Please enter a first name');
  }
  if (!customerUpdates.last_name?.trim()) {
    throw new Error('Please enter a last name');
  }
  if (!customerUpdates.phone?.trim()) {
    throw new Error('Please enter a phone number');
  }

  // Get current customer record
  const { data: currentCustomer, error: fetchError } = await supabase
    .from('customers')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) {
    console.error('Error fetching customer:', fetchError);
    throw new Error('Unable to load customer details. Please try again.');
  }

  if (!currentCustomer) {
    throw new Error('Customer not found. They may have been deleted or moved.');
  }

  // Create a clean updates object, removing view-specific fields that don't exist in the customers table
  // Type assertion to help TypeScript understand the structure
  const customerUpdatesTyped = customerUpdates as Partial<Customer & {
    last_booking_date?: string;
    total_bookings?: number;
    total_vehicles?: number;
    total_spent?: number;
    recent_bookings?: any[];
    vehicles?: any[];
  }>;

  // Now destructure with proper typing
  const {
    last_booking_date,
    total_bookings,
    total_vehicles,
    total_spent,
    recent_bookings,
    vehicles: _ignoredVehicles, // Ignore this field
    ...updateData
  } = customerUpdatesTyped;

  // Update customer
  const { data, error } = await supabase
    .from('customers')
    .update({
      ...currentCustomer,
      ...updateData,
      business_id: currentCustomer.business_id
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating customer:', error);
    throw new Error('Unable to update customer details. Please try again.');
  }

  // Handle vehicles separately if present
  if (vehicles && vehicles.length > 0) {
    // Get existing vehicles
    const { data: existingVehicles, error: fetchError } = await supabase
      .from('customer_vehicles')
      .select('id')
      .eq('customer_id', id);

    if (fetchError) {
      console.error('Error fetching vehicles:', fetchError);
      throw new Error('Unable to load vehicle details. Please try again.');
    }

    const existingIds = new Set(existingVehicles.map(v => v.id));
    const updatedIds = new Set(vehicles.filter(v => v.id).map(v => v.id));

    // Delete vehicles that are no longer in the list
    const vehiclesToDelete = Array.from(existingIds).filter(vehicleId => !updatedIds.has(vehicleId));
    if (vehiclesToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('customer_vehicles')
        .delete()
        .in('id', vehiclesToDelete);

      if (deleteError) {
        console.error('Error deleting vehicles:', deleteError);
        throw new Error('Unable to remove vehicles. Please try again.');
      }
    }

    // Update existing vehicles
    const vehiclesToUpdate = vehicles.filter(v => v.id && existingIds.has(v.id));
    for (const vehicle of vehiclesToUpdate) {
      const { id: vehicleId, ...vehicleUpdateData } = vehicle;
      
      if (!vehicleId) continue; // TypeScript safety check
      
      const { error: updateError } = await supabase
        .from('customer_vehicles')
        .update(vehicleUpdateData)
        .eq('id', vehicleId);

      if (updateError) {
        console.error('Error updating vehicle:', updateError);
        throw new Error('Unable to update vehicle details. Please try again.');
      }
    }

    // Insert new vehicles
    const vehiclesToInsert = vehicles.filter(v => !v.id);
    if (vehiclesToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from('customer_vehicles')
        .insert(
          vehiclesToInsert.map(vehicle => ({
            ...vehicle,
            customer_id: id
          }))
        );

      if (insertError) {
        console.error('Error adding vehicles:', insertError);
        throw new Error('Unable to add new vehicles. Please try again.');
      }
    }
  }

  // Auto-update map pin if address or coordinates changed
  const addressChanged = currentCustomer.address !== data.address;
  const coordinatesChanged = currentCustomer.lat !== data.lat || currentCustomer.lng !== data.lng;
  
  if ((addressChanged || coordinatesChanged) && data.lat && data.lng && data.address) {
    try {
      console.log('🎯 Auto-updating map pin for customer:', {
        name: `${data.first_name} ${data.last_name}`,
        addressChanged,
        coordinatesChanged,
        newAddress: data.address,
        newCoordinates: { lat: data.lat, lng: data.lng }
      });

      // Check if customer already has a map marker
      const { data: existingMarker, error: fetchMarkerError } = await supabase
        .from('address_markers')
        .select('id')
        .eq('customer_id', id)
        .eq('marker_type', 'customer')
        .single();

      if (fetchMarkerError && fetchMarkerError.code !== 'PGRST116') {
        console.error('❌ Error checking existing marker:', fetchMarkerError);
      } else if (existingMarker) {
        // Update existing marker
        const { error: updateMarkerError } = await supabase
          .from('address_markers')
          .update({
            latitude: data.lat,
            longitude: data.lng,
            address: data.address,
            notes: `Customer: ${data.type === 'business' ? data.first_name : `${data.first_name} ${data.last_name}`.trim()}`,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingMarker.id);

        if (updateMarkerError) {
          console.error('❌ Failed to update map pin:', updateMarkerError);
        } else {
          console.log('✅ Updated existing map pin for customer');
        }
      } else {
        // Create new marker
        const { error: createMarkerError } = await supabase
          .from('address_markers')
          .insert({
            business_id: data.business_id,
            customer_id: data.id,
            latitude: data.lat,
            longitude: data.lng,
            marker_type: 'customer',
            address: data.address,
            notes: `Customer: ${data.type === 'business' ? data.first_name : `${data.first_name} ${data.last_name}`.trim()}`,
            created_by: null
          });

        if (createMarkerError) {
          console.error('❌ Failed to create map pin:', createMarkerError);
        } else {
          console.log('✅ Created new map pin for customer');
        }
      }
    } catch (error) {
      console.error('❌ Exception during auto map pin update:', error);
      // Don't throw error - customer update should succeed even if pin update fails
    }
  } else if (!data.address || (!data.lat || !data.lng)) {
    // Remove map pin if customer no longer has address or coordinates
    try {
      const { error: deleteMarkerError } = await supabase
        .from('address_markers')
        .delete()
        .eq('customer_id', id)
        .eq('marker_type', 'customer');

      if (deleteMarkerError) {
        console.error('❌ Failed to remove map pin:', deleteMarkerError);
      } else {
        console.log('🗑️ Removed map pin for customer (no address/coordinates)');
      }
    } catch (error) {
      console.error('❌ Exception during map pin removal:', error);
    }
  }

  // Return the updated customer with all data
  return await getCustomer(id);
}

async function deleteCustomer(id: string) {
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export { deleteCustomer };

async function getCustomerVehicles(customerId: string) {
  const { data, error } = await supabase
    .from('customer_vehicles')
    .select('*')
    .eq('customer_id', customerId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as CustomerVehicle[];
}

export async function createCustomerVehicle(
  vehicle: Omit<CustomerVehicle, 'id' | 'created_at' | 'updated_at'>
) {
  const { data, error } = await supabase
    .from('customer_vehicles')
    .insert([vehicle])
    .select()
    .single();

  if (error) throw error;
  return data as CustomerVehicle;
}

export async function updateCustomerVehicle(
  id: string,
  updates: Partial<CustomerVehicle>
) {
  const { data, error } = await supabase
    .from('customer_vehicles')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CustomerVehicle;
}

export async function deleteCustomerVehicle(id: string) {
  const { error } = await supabase
    .from('customer_vehicles')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

async function getCustomerStats(businessId: string) {
  const { data: customers, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId);

  if (error) throw error;

  const stats = {
    totalCustomers: customers.length,
    activeCustomers: customers.filter(c => c.status === 'active').length,
    totalSpent: customers.reduce((sum, c) => sum + (c.amount_spent || 0), 0),
    averageSpent: customers.length > 0 
      ? customers.reduce((sum, c) => sum + (c.amount_spent || 0), 0) / customers.length 
      : 0
  };

  return stats;
}

/**
 * Finds an existing customer by phone number or email
 * @returns The matching customer if found, or null if no match
 */
export async function findExistingCustomer(
  businessId: string,
  email?: string | null,
  phone?: string | null
) {
  if (!email && !phone) {
    return null;
  }

  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Database connection is not available');
  }

  // Try to find by phone first (phone is usually more unique)
  if (phone) {
    const { data: phoneMatches, error: phoneError } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('phone', phone);
    
    if (phoneError) {
      console.error('Error checking for customer by phone:', phoneError);
      throw new Error('Unable to check for existing customers');
    }
    
    if (phoneMatches && phoneMatches.length > 0) {
      console.log(`Found customer with matching phone number: ${phone}`);
      return phoneMatches[0]; // Return the first matching customer
    }
  }
  
  // If no match by phone and we have an email, try email
  if (email) {
    const { data: emailMatches, error: emailError } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('email', email);
    
    if (emailError) {
      console.error('Error checking for customer by email:', emailError);
      throw new Error('Unable to check for existing customers');
    }
    
    if (emailMatches && emailMatches.length > 0) {
      console.log(`Found customer with matching email: ${email}`);
      return emailMatches[0]; // Return the first matching customer
    }
  }
  
  // No matches found
  return null;
}

/**
 * Get all suppliers for a business (customers where is_supplier = true and not deleted)
 */
export async function getSuppliers(businessId: string) {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .eq('is_supplier', true)
    .neq('status', 'deleted')
    .order('first_name');

  if (error) throw error;
  return data as Customer[];
}

/**
 * Create a new supplier (customer with is_supplier = true)
 */
export async function createSupplier(
  supplier: Omit<Customer, 'id' | 'created_at' | 'updated_at' | 'vehicles' | 'is_supplier'>
) {
  return createCustomer({
    ...supplier,
    is_supplier: true
  });
}

/**
 * Updates an existing customer with information from an online booking
 * @returns The updated customer record
 */
export async function updateCustomerFromBooking(
  customerId: string,
  updates: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    address?: string;
  }
) {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Database connection is not available');
  }

  // Get the existing customer
  const { data: existingCustomer, error: fetchError } = await supabase
    .from('customers')
    .select('*')
    .eq('id', customerId)
    .single();

  if (fetchError) {
    console.error('Error fetching customer to update:', fetchError);
    throw new Error('Unable to load customer details for update');
  }

  if (!existingCustomer) {
    throw new Error('Customer not found for update');
  }

  // Only update fields that are provided and different
  const updateData: Record<string, any> = {};
  
  if (updates.first_name && updates.first_name !== existingCustomer.first_name) {
    updateData.first_name = updates.first_name;
  }
  
  if (updates.last_name && updates.last_name !== existingCustomer.last_name) {
    updateData.last_name = updates.last_name;
  }
  
  if (updates.email && updates.email !== existingCustomer.email) {
    updateData.email = updates.email;
  }
  
  if (updates.phone && updates.phone !== existingCustomer.phone) {
    updateData.phone = updates.phone;
  }
  
  if (updates.address && updates.address !== existingCustomer.address) {
    updateData.address = updates.address;
  }
  
  // If there are no changes, return the existing customer
  if (Object.keys(updateData).length === 0) {
    return existingCustomer;
  }
  
  // Update the customer with the new information
  const { data: updatedCustomer, error: updateError } = await supabase
    .from('customers')
    .update({ 
      ...updateData,
      updated_at: new Date().toISOString()
    })
    .eq('id', customerId)
    .select()
    .single();

  if (updateError) {
    console.error('Error updating customer:', updateError);
    throw new Error('Unable to update customer information');
  }

  return updatedCustomer;
}